import { useState, useEffect, useRef } from "react";
import { C } from "./ui";

// Búsqueda global: indexa animales, potreros, zonas, insumos, tareas, personal y
// maquinaria del campo activo. Al elegir un resultado, navega a su sección.
export function Buscador({ open, onClose, onNav, potreros = [], insumos = [], zonas = [], tareas = [], maquinarias = [], empleados = [], stockSanidad = [] }) {
  const [q, setQ] = useState("");
  const inputRef = useRef(null);

  useEffect(() => { if (open) { setQ(""); setTimeout(() => inputRef.current?.focus(), 30); } }, [open]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && open) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const term = q.trim().toLowerCase();
  const match = (...vals) => vals.some(v => (v || "").toString().toLowerCase().includes(term));

  const grupos = [];
  if (term.length >= 1) {
    const animales = potreros.flatMap(p => (p.animalesAsignados || []).map(a => ({ ...a, potreroNombre: p.nombre })))
      .filter(a => match(a.caravana, a.categoria, a.raza, a.especie))
      .slice(0, 8)
      .map(a => ({ ic: "🐄", titulo: `#${a.caravana || "—"} · ${a.categoria || ""}`, meta: `${a.especie || ""} · 📍 ${a.potreroNombre}`, nav: "rodeo" }));
    const pots = potreros.filter(p => match(p.nombre, p.pasto)).slice(0, 6)
      .map(p => ({ ic: "🌱", titulo: p.nombre, meta: `${p.pasto || ""} · ${(p.ha || 0).toFixed(1)} ha`, nav: "mapa" }));
    const zns = zonas.filter(z => match(z.nombre, z.tipo)).slice(0, 6)
      .map(z => ({ ic: "🐔", titulo: z.nombre, meta: z.tipo || "Zona", nav: "zonas" }));
    const ins = insumos.filter(i => match(i.nombre, i.categoria)).slice(0, 6)
      .map(i => ({ ic: "📦", titulo: i.nombre, meta: `${i.cantidad || 0} ${i.unidad || ""} · ${i.categoria || ""}`, nav: "insumos" }));
    const san = stockSanidad.filter(s => match(s.nombre, s.tipo)).slice(0, 6)
      .map(s => ({ ic: "💉", titulo: s.nombre, meta: `${s.cantidad || 0} ${s.unidad || ""} · Sanidad`, nav: "sanidad" }));
    const trs = tareas.filter(t => match(t.titulo, t.tipo)).slice(0, 6)
      .map(t => ({ ic: "🗓️", titulo: t.titulo || t.tipo, meta: `${t.tipo || ""} · ${t.fecha || ""}`, nav: "agenda" }));
    const maq = maquinarias.filter(m => match(m.nombre, m.marca, m.modelo)).slice(0, 5)
      .map(m => ({ ic: "🚜", titulo: m.nombre, meta: [m.marca, m.modelo].filter(Boolean).join(" ") || "Maquinaria", nav: "gestion" }));
    const emp = empleados.filter(e => match(e.nombre, e.rol)).slice(0, 5)
      .map(e => ({ ic: "👷", titulo: e.nombre, meta: e.rol || "Personal", nav: "gestion" }));

    [["Animales", animales], ["Potreros", pots], ["Zonas", zns], ["Insumos", ins], ["Sanidad", san], ["Tareas", trs], ["Maquinaria", maq], ["Personal", emp]]
      .forEach(([label, items]) => { if (items.length) grupos.push({ label, items }); });
  }
  const total = grupos.reduce((s, g) => s + g.items.length, 0);

  const ir = (nav) => { onNav?.(nav); onClose(); };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(31,18,10,0.45)", zIndex: 3000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "10vh 16px 16px" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.white, width: "100%", maxWidth: 560, borderRadius: 14, boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden", maxHeight: "78vh", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "13px 16px", borderBottom: `1px solid ${C.arenaDark}` }}>
          <span style={{ fontSize: 17 }}>🔎</span>
          <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar caravana, potrero, insumo, tarea…"
            style={{ flex: 1, border: 0, outline: 0, background: "transparent", fontSize: 15, color: C.texto, fontFamily: "sans-serif" }} />
          <button onClick={onClose} style={{ background: C.arena, border: "none", borderRadius: 7, padding: "3px 8px", fontSize: 11, color: C.muted, cursor: "pointer", fontFamily: "monospace" }}>ESC</button>
        </div>
        <div style={{ overflowY: "auto", padding: "6px 8px 10px" }}>
          {term.length === 0 ? (
            <div style={{ padding: "26px 16px", textAlign: "center", color: C.mutedLight, fontSize: 13 }}>Escribí para buscar en todo el campo.</div>
          ) : total === 0 ? (
            <div style={{ padding: "26px 16px", textAlign: "center", color: C.muted, fontSize: 13 }}>Sin resultados para “{q}”.</div>
          ) : grupos.map(g => (
            <div key={g.label} style={{ marginBottom: 4 }}>
              <div style={{ fontFamily: "monospace", fontSize: 10, textTransform: "uppercase", letterSpacing: ".08em", color: C.mutedLight, padding: "8px 10px 3px", fontWeight: 600 }}>{g.label}</div>
              {g.items.map((r, i) => (
                <div key={i} onClick={() => ir(r.nav)} style={{ display: "flex", alignItems: "center", gap: 11, padding: "8px 10px", borderRadius: 9, cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.background = C.arena} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <span style={{ fontSize: 16, width: 22, textAlign: "center" }}>{r.ic}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: C.tierra, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.titulo}</div>
                    <div style={{ fontSize: 11.5, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.meta}</div>
                  </div>
                  <span style={{ fontSize: 12, color: C.mutedLight }}>↵</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
